import Button from '@eduzz/houston-ui/Button';
import FieldText from '@eduzz/houston-ui/Forms/Text';
import React, { useEffect, useState } from 'react';
import { IUser } from 'interface/user';
import ImageUploader from './ImageUploader';
import { useFormik } from 'formik';
import { Typography, makeStyles, Box } from '@material-ui/core';
import EmailOutline from '@eduzz/houston-icons/EmailOutline';
import LockedOutline from '@eduzz/houston-icons/LockedOutline';
import SaveOutline from '@eduzz/houston-icons/SaveOutline';
import profileService from 'services/profile';
import * as yup from 'yup';
import ProfileFormSkeleton from './Skeleton';
import InputAdornment from '@material-ui/core/InputAdornment';
import EyeOnOutline from '@eduzz/houston-icons/EyeOnOutline';
import EyeOffOutline from '@eduzz/houston-icons/EyeOffOutline';
import CenteredPage from 'components/Shared/CenteredPage';
interface IProps {
  formSuccess?: () => void;
  noPass?: boolean;
}

interface IState {
  name: string;
  email: string;
  currentpass: string;
  password: string;
  newPasswordConfirm?: string;
  avatar: string;
}

const useStyles = makeStyles((theme) => ({
  formContainer: {
    minWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& *::placeholder': {
      color: '#97b4c2 !important',
    },
    '& b': {
      fontWeight: 700,
    },
  },
  input: {
    maxWidth: 440,
    marginTop: theme.spacing(6),
    '& label': {
      fontWeight: 600,
      color: `${theme.palette.grey[500]} !important`,
    },
    '& Mui-focused': {
      color: `${theme.palette.grey[500]} !important`,
    },
    '& fieldset': {
      borderColor: `${theme.palette.grey[300]} !important`,
    },
    '& input, textarea': {
      color: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
    },
    '& .Mui-disabled': {
      background: 'none',
      color: theme.palette.grey[300],
    },
    '& .houston-icon': {
      color: theme.palette.common.white,
    },
  },
  whiteColor: {
    color: theme.palette.common.white,
  },
  icon: {
    cursor: 'pointer',
  },
  hint: {
    color: theme.palette.grey[300],
    maxWidth: 440,
  },
}));

const validationSchema = yup.object({
  name: yup.string().required('O campo nome é obrigatório'),
  password: yup.string().when('currentpass', {
    is: (val: string) => val?.length > 0,
    then: () => yup.string().required('A senha é obrigatória'),
  }),
  currentpass: yup.string(),
  newPasswordConfirm: yup.string().test('Confirm Password', 'As senhas não são iguais', (val, ctx) => {
    return val === ctx.parent.password;
  }),
});

const handleShowPassword = (state: boolean, setState: React.Dispatch<React.SetStateAction<boolean>>) => {
  const handleShowInput = () => {
    setState((prev) => !prev);
  };

  return (
    <InputAdornment onClick={handleShowInput} position='end'>
      {state ? <EyeOnOutline /> : <EyeOffOutline />}
    </InputAdornment>
  );
};

const NewPerfilForm: React.FC<IProps> = ({ formSuccess, noPass }) => {
  const classes = useStyles({});
  const [data, setData] = useState<Partial<IUser & IState>>({
    currentpass: '',
    password: '',
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const profileFormik = useFormik<Partial<IUser & IState>>({
    initialValues: data,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      profileService.updateProfileInfo(values).then(() => {
        profileFormik.setFieldValue('name', values.name);
        profileFormik.setFieldValue('avatar', values.avatar);
        profileFormik.setSubmitting(false);
      });
    },
  });

  useEffect(() => {
    profileService.getProfileInfo().then((res) => {
      setData((values) => {
        const data = Object.assign(values, {
          name: res.name,
          avatar: res.avatar,
          email: res.email,
          type: res.type,
        });
        return { ...data };
      });
    });
  }, []);

  if (!data.name)
    return (
      <CenteredPage marginTop={12}>
        <ProfileFormSkeleton />
      </CenteredPage>
    );

  return (
    <CenteredPage marginTop={12}>
      <div className={classes.formContainer}>
        <Typography variant='h3' className={classes.whiteColor} component='b'>
          Dados do perfil
        </Typography>
        <ImageUploader
          label='Foto de Perfil'
          width={250}
          height={250}
          value={profileFormik.values.avatar}
          disabled={profileFormik.isSubmitting}
          onChange={profileFormik.handleChange('avatar')}
        />
        <FieldText
          margin='none'
          fullWidth
          type='text'
          className={classes.input}
          name='name'
          id='profile-name'
          errorMessage={profileFormik.errors.name}
          placeholder='Seu nome'
          value={profileFormik.values.name}
          disabled={profileFormik.isSubmitting}
          label='Nome'
          onChange={profileFormik.handleChange('name')}
        />
        <FieldText
          margin='none'
          fullWidth
          id='profile-email'
          type='text'
          name='email'
          label='E-mail'
          value={profileFormik.values.email}
          disabled
          endAdornment={<EmailOutline />}
          className={classes.input}
          onChange={profileFormik.handleChange('email')}
        />

        {(profileFormik.values.type !== 1 || noPass) && (
          <>
            <Box marginTop={12}>
              <Typography variant='body1' className={classes.whiteColor}>
                Troca de senha
              </Typography>
            </Box>
            <FieldText
              margin='none'
              fullWidth
              id='profile-password'
              type={showCurrentPassword ? 'text' : 'password'}
              label='Senha atual'
              name='currentpass'
              errorMessage={profileFormik.errors.currentpass}
              placeholder='Coloque sua senha atual aqui'
              endAdornment={
                <div className={classes.icon}>{handleShowPassword(showCurrentPassword, setShowCurrentPassword)}</div>
              }
              className={classes.input}
              value={profileFormik.values.currentpass}
              disabled={profileFormik.isSubmitting}
              onChange={profileFormik.handleChange('currentpass')}
            >
            </FieldText>
            <FieldText
              margin='none'
              fullWidth
              id='profile-new-password'
              label='Nova senha'
              type={showPassword ? 'text' : 'password'}
              placeholder='Coloque sua nova senha aqui'
              name='password'
              errorMessage={profileFormik.errors.password}
              endAdornment={<div className={classes.icon}>{handleShowPassword(showPassword, setShowPassword)}</div>}
              value={profileFormik.values.password}
              disabled={profileFormik.isSubmitting}
              className={classes.input}
              onChange={profileFormik.handleChange('password')}
            />
            <FieldText
              margin='none'
              fullWidth
              id='profile-new-password-2'
              placeholder='Coloque sua nova senha aqui'
              type={showConfirmPassword ? 'text' : 'password'}
              label='Confirme sua nova senha'
              name='newPasswordConfirm'
              errorMessage={profileFormik.errors.newPasswordConfirm}
              endAdornment={
                <div className={classes.icon}>{handleShowPassword(showConfirmPassword, setShowConfirmPassword)}</div>
              }
              className={classes.input}
              value={profileFormik.values.newPasswordConfirm}
              disabled={profileFormik.isSubmitting}
              onChange={profileFormik.handleChange('newPasswordConfirm')}
            />
          </>
        )}

        <Box marginTop={6}>
          <Button
            id='profile-btn-save'
            loading={profileFormik.isSubmitting}
            onClick={profileFormik.submitForm}
            startIcon={<SaveOutline />}
            type='submit'
          >
            Salvar Alterações
          </Button>
        </Box>

        {!(profileFormik.values.type !== 1 || noPass) && (
          <Box marginTop={8}>
            <Typography variant='body2' className={classes.hint}>
              Para alterar sua senha, clique no botão abaixo para que você seja direcionado ao Órbita e dê andamento na
              troca da sua senha.
            </Typography>
          </Box>
        )}

        {profileFormik.values.type === 1 && (
          <Box marginTop={6}>
            <a href={'https://orbita.eduzz.com/producer/profile/edit'} target='_blank' rel='noreferrer'>
              <Button startIcon={<LockedOutline />} type='submit'>
                Alterar Senha
              </Button>
            </a>
          </Box>
        )}
      </div>
    </CenteredPage>
  );
};

export default NewPerfilForm;
